import { REVALIDATE_TIME } from "@/constants/cache"
import { HttpClient } from "@/app/api/HttpClient"
import { API_PREFIX_CATEGORY_PATH } from "@/constants/prefix-path-api"
import type { SiteId } from "@/sites/config"
import { AttributesCategory, CategoryLocalizationPayload, FilterSearchParams } from "@/types/types"
import { message } from "antd"
import { AxiosRequestConfig } from "axios"

const inFlightCategoryRequests = new Map<string, Promise<any>>()

// Category lưu site ở field `domain` trực tiếp (không qua relation `websites`).
// Cụm tamdaone gom mọi domain ≠ tamdamedia.eu (tamdagroup/tamdafoods/tamdaoc/
// tamdaone/express) — cùng quy tắc với buildCategoriesFilter trong sitemap-services.
// Không truyền siteId → không lọc (admin dùng bộ lọc riêng của AdminHttpClient).
export const buildCategoryDomainFilter = (siteId?: SiteId) => {
  if (!siteId) return undefined
  return siteId === "tamdamedia.eu" ? { $eq: "tamdamedia.eu" } : { $ne: "tamdamedia.eu" }
}

// Predicate tương ứng với buildCategoryDomainFilter, để lọc danh mục đã được
// populate sẵn trên bài viết (không cần gọi lại Strapi). Không có siteId → không
// lọc. Dùng cho hiển thị danh mục + chọn danh mục "hợp lệ" khi lấy related posts.
export const categoryBelongsToSite = (domain?: string, siteId?: SiteId): boolean => {
  if (!siteId) return true
  return siteId === "tamdamedia.eu" ? domain === "tamdamedia.eu" : domain !== "tamdamedia.eu"
}

export const fetchCategories = async (
  searchTerms: { [key: string]: string } = {},
  publicationState: string = "live",
) => {
  const params: FilterSearchParams = {
    sort: "createdAt:desc",
    filters: {},
    pagination: {
      pageSize: "100",
      // Ask Strapi to return total/pageCount so the list can paginate.
      withCount: true,
    },
    publicationState,
  }

  const excludedKeys = ["current", "pageSize", "start", "limit", "total", "page"]

  Object.keys(searchTerms).forEach(key => {
    const value = searchTerms[key]

    if (key === "populate") {
      params.populate = value
    } else if (key === "locale") {
      params.locale = value
    } else if (key === "sort") {
      params.sort = value
    } else if (key === "publishedAtNull") {
      params.filters["publishedAt"] = { $null: value === "true" }
    } else if (!excludedKeys.includes(key)) {
      if (value) {
        params.filters[key] = { $containsi: value }
      }
    } else if (value) {
      params.pagination = {
        ...params.pagination,
        [key]: value,
      }
    }
  })

  const requestKey = JSON.stringify({ publicationState, params })
  const existingRequest = inFlightCategoryRequests.get(requestKey)

  if (existingRequest) {
    return await existingRequest
  }

  const requestPromise = HttpClient.get(`${API_PREFIX_CATEGORY_PATH}`, { params }) as Promise<any>
  inFlightCategoryRequests.set(requestKey, requestPromise)

  try {
    const response = await requestPromise
    return response
  } catch (error) {
    message.error("Không thể lấy danh mục. Vui lòng thử lại.")
  } finally {
    inFlightCategoryRequests.delete(requestKey)
  }
}

export const fetchCategoryById = async (id: string) => {
  try {
    const response = await HttpClient.get(`${API_PREFIX_CATEGORY_PATH}/${id}`)
    return response
  } catch (error) {
    message.error("Không thể lấy danh mục. Vui lòng thử lại.")
  }
}

export const updateCategory = async (id: string, updatedData: AttributesCategory) => {
  return await HttpClient.put(`${API_PREFIX_CATEGORY_PATH}/${id}`, { data: updatedData })
}

export const createCategory = async (data: AttributesCategory) => {
  return await HttpClient.post(`${API_PREFIX_CATEGORY_PATH}`, { data })
}

export const deleteCategory = async (id: number) => {
  return await HttpClient.delete(`${API_PREFIX_CATEGORY_PATH}/${id}`)
}

export const getCategoryById = async (id: number) => {
  return await HttpClient.get(
    `${API_PREFIX_CATEGORY_PATH}/${id}?populate[banner][fields][0]=url&populate[seo][populate]=*`,
  )
}

// `populate` mặc định để trống (không populate quan hệ nào) vì hầu hết callers
// (vd. duplicateCategory) chỉ cần field thường (name, slug, domain...). Truyền
// rõ tên quan hệ cần khi cần populate (vd. "localizations") — tránh populate=*
// kéo theo quan hệ `posts` (hàng chục nghìn bài viết) không ai dùng tới.
export const getCategoryDetail = async (id: number, populate?: string) => {
  try {
    const query = populate ? `?populate=${populate}` : ""
    const response = await HttpClient.get(`${API_PREFIX_CATEGORY_PATH}/${id}${query}`)
    return response
  } catch (error) {
    message.error("Không thể lấy chi tiết danh mục. Vui lòng thử lại.")
  }
}

export const createCategoryLocalization = async (id: number, data: CategoryLocalizationPayload) => {
  try {
    const response = await HttpClient.post(`${API_PREFIX_CATEGORY_PATH}/${id}/localizations`, data)
    return response
  } catch (error) {
    message.error("Không thể tạo bản dịch. Vui lòng thử lại.")
  }
}

export const fetchTags = async () => {
  return await HttpClient.get(`/api/tags`)
}

export const checkSlugCategory = async (slug: string, locale?: string, siteId?: SiteId) => {
  const domainFilter = buildCategoryDomainFilter(siteId)
  const params: AxiosRequestConfig["params"] = {
    // `playlistUrl` cần cho category loại video: nó trỏ tới playlist YouTube để
    // lấy data từ collection `youtube-videos` (nguồn mới) thay vì `videos` (cũ).
    fields: ["id", "name", "slug", "type", "domain", "playlistUrl"],
    filters: {
      slug: { $eq: slug },
      // Lọc ngay ở Strapi thay vì lấy data[0] rồi mới đối chiếu domain: 2 site có
      // thể trùng slug (vd `thong-bao`), lấy [0] dễ ra category của site kia.
      ...(domainFilter ? { domain: domainFilter } : {}),
    },
    publicationState: "live",
    ...(locale ? { locale } : {}),
  }
  return await HttpClient.get(`${API_PREFIX_CATEGORY_PATH}`, {
    params,
    revalidate: REVALIDATE_TIME,
  })
}

export const fetchCategoryDetailBySlug = async (slug: string, locale?: string, siteId?: SiteId) => {
  try {
    const domainFilter = buildCategoryDomainFilter(siteId)
    const params: any = {
      filters: {
        slug: { $eq: slug },
        ...(domainFilter ? { domain: domainFilter } : {}),
      },
      "populate[seo][populate]": "*",
      publicationState: "live",
    }
    if (locale) {
      params.locale = locale
    }
    const response = await HttpClient.get(`${API_PREFIX_CATEGORY_PATH}`, {
      params,
      revalidate: REVALIDATE_TIME,
    })
    return response
  } catch (error) {
    message.error("Không thể lấy chi tiết danh mục. Vui lòng thử lại.")
  }
}
